import { Checkbox, Datepicker, Label, Select, TextInput } from 'flowbite-react';
import { useEffect, useRef, useState } from 'react';
import { useCallback } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import {
  FileUpload,
  type UploadingFile,
} from '../../../../../shared/ui/fileUpload/fileUpload';
import { Textarea } from '../../../../../shared/ui';

function FileUploadControl({
  value,
  onChange,
  props,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  props?: Record<string, unknown>;
}) {
  const list = (Array.isArray(value) ? value : []) as UploadingFile[];
  const accept = (props?.accept as string) ?? '.pdf';
  const maxSize = (props?.maxSize as number) ?? 10;
  const multiple = (props?.multiple as boolean) ?? true;
  const showInfoIcon = (props?.showInfoIcon as boolean) ?? false;
  const label = (props?.label as string) ?? '';

  const handleFileSelect = useCallback(
    (selected: File[]) => {
      const newItems: UploadingFile[] = selected.map((file, index) => ({
        id: `fu-${Date.now()}-${index}`,
        file,
        status: 'pending',
        progress: 0,
      }));
      onChange([...list, ...newItems]);
    },
    [list, onChange]
  );

  const handleFileRemove = useCallback(
    (fileId: string) => {
      onChange(list.filter((f) => f.id !== fileId));
    },
    [list, onChange]
  );

  return (
    <div className="space-y-2">
      {(label || showInfoIcon) && (
        <div className="flex items-center gap-2 mb-1">
          {label ? (
            <span className="text-sm font-medium text-gray-900">{label}</span>
          ) : null}
          {showInfoIcon ? (
            <HiInformationCircle className="w-4 h-4 text-green-500 shrink-0" />
          ) : null}
        </div>
      )}
      <FileUpload
        onFileSelect={handleFileSelect}
        onFileRemove={handleFileRemove}
        uploadingFiles={list}
        accept={accept}
        maxSize={maxSize}
        multiple={multiple}
      />
    </div>
  );
}

export const FlowbiteControls = {
  text: ({ value, onChange, onBlur, props }: any) => (
    <TextInput
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      {...props}
    />
  ),
  textarea: ({ value, onChange, onBlur, props }: any) => (
    <Textarea
      value={value ?? ''}
      onChange={(value) => onChange(value)}
      onBlur={onBlur}
      noResize
      {...props}
    />
  ),
  number: ({ value, onChange, onBlur, props }: any) => (
    <TextInput
      type="number"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      {...props}
    />
  ),
  checkbox: ({ value, onChange, onBlur, props }: any) => (
    <div className="flex items-center gap-2">
      <Checkbox
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        onBlur={onBlur}
        {...props}
      />
      {props?.label && <Label htmlFor={props.id}>{props.label}</Label>}
    </div>
  ),
  datepicker: ({ value, onChange, onBlur, props }: any) => {
    const [showPicker, setShowPicker] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Закрытие при клике вне
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setShowPicker(false);
        }
      };

      if (showPicker) {
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showPicker]);
    const formatDate = (value: string | Date) => {
      let date;
      if (typeof value === 'string') {
        date = new Date(value);
        if (isNaN(date.getTime())) {
          return null;
        }
      } else {
        date = value;
      }

      const formatter = new Intl.DateTimeFormat('ru-RU', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });

      return formatter.format(date);
    };
    const handleDateSelection = (date: Date | null) => {
      if (date) {
        onChange(date);
      }
      setShowPicker(false);
    };

    return (
      <div className="relative w-full" ref={containerRef}>
        <TextInput
          className="w-full"
          onFocus={() => setShowPicker(true)}
          value={formatDate(value) ?? ''}
          onBlur={onBlur}
          {...props}
        ></TextInput>
        {showPicker && (
          <Datepicker
            language="ru-RU"
            weekStart={1}
            value={null}
            onChange={handleDateSelection}
            className="w-full absolute z-50"
            showClearButton={false}
            showTodayButton={false}
            defaultValue={value ? new Date(value) : new Date()}
            autoHide
            readOnly
            inline
          />
        )}
      </div>
    );
  },
  select: ({ value, onChange, onBlur, props, options }: any) => (
    <Select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      {...props}
    >
      <option value="" disabled>
        {props?.placeholder ?? 'Выберите…'}
      </option>
      {options?.map((o: any) => (
        <option key={String(o.value)} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  ),
  fileUpload: ({ value, onChange, props }: any) => (
    <FileUploadControl value={value} onChange={onChange} props={props} />
  ),
};
